/**
 * Pure helpers for the artwork wall: the zoom ladder, version dedupe, and the
 * localStorage preference shape. No React, no Firebase — all unit-testable.
 */
import { clamp, isPlainObject, sortBy } from "lodash"

import {
  type StudentArtwork,
  type StudentConstructor,
} from "#customTypes/composedTypes/studentTypes"

import {
  type ArtworkWallItem,
  type ArtworkWallPrefs,
  WALL_LAYOUT,
  type WallLayout,
} from "./artworkWallTypes"

/**
 * Six zoom levels: columns across, then the full-height strip. Index order is
 * the "+" direction — bigger tiles, fewer across, strip last.
 */
export const ZOOM_LEVELS = [6, 5, 4, 3, 2, "strip"] as const

export type ZoomLevel = (typeof ZOOM_LEVELS)[number]

export const STRIP_ZOOM_INDEX = ZOOM_LEVELS.length - 1
export const MAX_GRID_ZOOM_INDEX = STRIP_ZOOM_INDEX - 1
export const DEFAULT_ZOOM_INDEX = 2

export const clampZoomIndex = (index: number): number =>
  clamp(Math.round(index), 0, STRIP_ZOOM_INDEX)

export const clampGridZoomIndex = (index: number): number =>
  clamp(Math.round(index), 0, MAX_GRID_ZOOM_INDEX)

export const isStripZoom = (index: number): boolean =>
  index === STRIP_ZOOM_INDEX

/** Columns for a grid zoom level; `null` for the strip. */
export const getColumnsForZoom = (index: number): number | null => {
  const level = ZOOM_LEVELS[clampZoomIndex(index)]
  return level === "strip" ? null : level
}

export const stepZoomIndex = (index: number, delta: number): number =>
  clampZoomIndex(index + delta)

export const describeZoomLevel = (index: number): string => {
  const level = ZOOM_LEVELS[clampZoomIndex(index)]
  return level === "strip" ? "Strip" : `${level} across`
}

// ---------------------------------------------------------------------------
// Version dedupe
// ---------------------------------------------------------------------------

export interface DedupedWallItems {
  /** Input order, filtered to the winner of each version group. */
  items: ArtworkWallItem[]
  /** Group size by winning item id; absent means a single version. */
  versionCounts: Record<string, number>
}

const versionRank = (item: ArtworkWallItem, index: number): number[] => [
  item.versionOrder ?? Number.NEGATIVE_INFINITY,
  item.artwork.timeCreated ?? Number.NEGATIVE_INFINITY,
  index,
]

const compareRank = (a: number[], b: number[]): number => {
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return a[i] > b[i] ? 1 : -1
  }
  return 0
}

/**
 * "Latest versions": within each `versionGroupKey` keep the item with the
 * highest `versionOrder` (then newest `timeCreated`, then last in input).
 * Items without a key pass through untouched.
 */
export const dedupeLatestVersions = (
  items: readonly ArtworkWallItem[]
): DedupedWallItems => {
  const winners = new Map<string, { index: number; rank: number[] }>()
  const groupSizes = new Map<string, number>()

  items.forEach((item, index) => {
    const key = item.versionGroupKey
    if (!key) return
    groupSizes.set(key, (groupSizes.get(key) ?? 0) + 1)
    const rank = versionRank(item, index)
    const current = winners.get(key)
    if (!current || compareRank(rank, current.rank) > 0) {
      winners.set(key, { index, rank })
    }
  })

  const versionCounts: Record<string, number> = {}
  const kept = items.filter((item, index) => {
    const key = item.versionGroupKey
    if (!key) return true
    const winner = winners.get(key)
    if (winner?.index !== index) return false
    const size = groupSizes.get(key) ?? 1
    if (size > 1) versionCounts[item.id] = size
    return true
  })

  return { items: kept, versionCounts }
}

// ---------------------------------------------------------------------------
// Preferences (localStorage — server-side prefs are deferred per the epic;
// a user-doc field would also break the zero-DB-change rule)
// ---------------------------------------------------------------------------

export const ARTWORK_WALL_PREFS_KEY = "jarvis-artwork-wall-prefs"

export const DEFAULT_WALL_PREFS: ArtworkWallPrefs = {
  layout: WALL_LAYOUT.SQUARE,
  zoomIndex: DEFAULT_ZOOM_INDEX,
}

const isWallLayout = (value: unknown): value is WallLayout =>
  value === WALL_LAYOUT.SQUARE || value === WALL_LAYOUT.FULL

/**
 * A stored value that is missing, not JSON, or the wrong shape is ordinary
 * input (an older build wrote it, or nothing did), so each field falls back to
 * its default independently. Storage access errors are not caught here.
 */
export const parseWallPrefs = (raw: string | null): ArtworkWallPrefs => {
  if (!raw) return DEFAULT_WALL_PREFS
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return DEFAULT_WALL_PREFS
  }
  if (!isPlainObject(parsed)) return DEFAULT_WALL_PREFS
  const candidate = parsed as Partial<Record<keyof ArtworkWallPrefs, unknown>>
  return {
    layout: isWallLayout(candidate.layout)
      ? candidate.layout
      : DEFAULT_WALL_PREFS.layout,
    zoomIndex:
      typeof candidate.zoomIndex === "number" &&
      Number.isFinite(candidate.zoomIndex)
        ? clampGridZoomIndex(candidate.zoomIndex)
        : DEFAULT_WALL_PREFS.zoomIndex,
  }
}

export const readWallPrefs = (
  storage: Pick<Storage, "getItem">,
  key: string = ARTWORK_WALL_PREFS_KEY
): ArtworkWallPrefs => parseWallPrefs(storage.getItem(key))

export const writeWallPrefs = (
  storage: Pick<Storage, "setItem">,
  prefs: ArtworkWallPrefs,
  key: string = ARTWORK_WALL_PREFS_KEY
): void => {
  storage.setItem(
    key,
    JSON.stringify({
      layout: prefs.layout,
      zoomIndex: clampGridZoomIndex(prefs.zoomIndex),
    })
  )
}

// ---------------------------------------------------------------------------
// Item builders for surfaces that start from student docs
// ---------------------------------------------------------------------------

/** Steps of one portfolio project, scoped to the student, are one version group. */
export const getVersionGroupKey = (
  studentId: string,
  artwork: Pick<StudentArtwork, "projectId">
): string | undefined =>
  artwork.projectId ? `${studentId}:${artwork.projectId}` : undefined

/**
 * Flattens `students[].artwork` into wall items, newest first. Used by the
 * debug dashboard; real surfaces will typically scope by class or student.
 * `isHidden` entries are excluded — this is the single choke point for both
 * the debug wall and the Quality Check surface.
 */
export const buildWallItemsFromStudents = (
  students: readonly StudentConstructor[]
): ArtworkWallItem[] => {
  const items: ArtworkWallItem[] = []
  for (const student of students) {
    for (const artwork of Object.values(student.artwork ?? {})) {
      if (artwork.isHidden === true) continue
      items.push({
        id: artwork.id,
        artwork,
        studentId: student.id,
        studentName: student.name,
        caption: artwork.title
          ? `${artwork.title} — ${student.name}`
          : `Artwork by ${student.name}`,
        versionGroupKey: getVersionGroupKey(student.id, artwork),
        versionOrder: artwork.timeCreated,
      })
    }
  }
  return sortBy(items, (item) => -(item.artwork.timeCreated ?? 0))
}
